import React, { useRef, useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import SVG from 'react-inlinesvg';
import classNames from './MyClassesView.module.scss';
import classes from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { UseMyClassesInterface } from './UseMyClasses';
import { registerService } from '@/services/register';

import TheButton from '@/components/TheButton/TheButton';
import StudentsTable from '@/components/StudentsTable/StudentsTable';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import PageHeaderFiltersBar from '@/components/school-management/GenericTable/PageHeader/PageHeaderFiltersBar';
import ClassEditor from '@/components/ClassEditor/ClassEditor';
import NoClasses from '@/components/NoClasses/NoClasses';
import { Transition } from 'react-transition-group';
import AppBackdrop from '@/components/AppBackdrop';
import AppModal from '@/components/AppModal';
import CsvDropzone, { CsvDropzoneRef } from '@/components/CsvDropzone/CsvDropzone';
import UploadCsvErrorModal from '@/components/UploadCsvErrorModal/UploadCsvErrorModal';
import CommunicationProblemModal from '@/components/CommunicationProblemModal/CommunicationProblemModal';
import DealLimitReachedMessage from '@/components/DealLimitReachedMessage/DealLimitReachedMessage';
import LoaderModal from '@/components/LoaderModal/LoaderModal';
import { LEVEL_CONTROL_DEFAULTS } from '@/school-management/utils/levelControl';

import closeIcon from '@/assets/svg/close.svg';
import leftArrow from '@/assets/svg/left-arrow.svg';
import download from '@/assets/svg/download.svg';
import syncIcon from '@/assets/svg/sync.svg';
import deleteIcon from '@/assets/svg/delete.svg';
import { useAppDispatch } from '@/store';
import teacherDashboardThunks from '@/store/thunks/teacher-dashboard';
import GoogleClassesModal from '@/components/GoogleClassesModal/GoogleClassesModal';
import {
  BaseGoogleCourses,
  EmptyModalState,
  GoogleClassAction,
  GoogleClassOperation,
  SyncGoogleCourses,
} from '@/types/register';
import GoogleClassesResultModal from '@/components/GoogleClassesResultModal/GoogleClassesResultModal';
import common from '@/utils/common';
import GoogleEmptyClassesModal from '@/components/GoogleEmptyClassesModal/GoogleEmptyClassesModal';
import GoogleDeleteClassConfirmationModal from '@/components/GoogleDeleteClassConfirmationModal/GoogleDeleteClassConfirmationModal';
import GoogleErrorClassesModal from '@/components/GoogleErrorClassesModal/GoogleErrorClassesModal';
import commonUtils from '@/utils/common';
import GenericTable from '@/components/school-management/GenericTable/GenericTable';
import Button from '@/components/school-management/Button/Button';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import { classService } from '@/school-management/services/class';
import processing, { XlsxData, XlsxTable } from '@/school-management/utils/processing';
import { createAdminClassesReportColumns } from '@/features/classes-report/classesReportTableColumns';
import { CLASSES_REPORT_PAGE_SIZE } from '@/features/classes-report/buildGroupsReportQueryParams';
import {
  classesReportTableRowToTeacherGroup,
  normalizeGroupReportItemsToTableRows,
} from '@/features/classes-report/normalizeGroupReportRows';
import adminClassStyles from '@/components/school-management/SchoolDetailsPage/styles.module.scss';
import DeleteClassModal from '@/components/school-management/SchoolDetailsPage/DeleteClassModal';
import ClassCodeModal from '@/components/school-management/ClassCodeModal/ClassCodeModal';
import ClassReport from '@/components/school-management/StudentDataModal/GenerateReport/ClassReport';
import TeacherMyClassesStatistics from './TeacherMyClassesStatistics';
import ClassModalsManager from '@/components/school-management/SchoolDetailsPage/ClassModalsManager';
import { EventDispatcherContext, DispatcherEvents } from '@/context/eventDispatcherContext';

const MyClassesViewDesktop = ({ myClasses }: { myClasses: UseMyClassesInterface }) => {
  const { t } = useAdminTranslation();
  const dispatch = useAppDispatch();
  const csvDropzoneRef = useRef<CsvDropzoneRef | null>(null);
  const eventDispatcherContext = useContext(EventDispatcherContext);

  const [showDealLimitReachedModal, setShowDealLimitReachedModal] = useState<boolean>(false);
  const [isOrganizationDetailsLoading, setIsOrganizationDetailsLoading] = useState<boolean>(false);
  const [allowAddingStudents, setAllowAddingStudents] = useState<boolean>(true);
  const [googleClasses, setGoogleClasses] = useState<BaseGoogleCourses[]>([]);
  const [googleClassesDelete, setGoogleClassesDelete] = useState<SyncGoogleCourses[]>([]);
  const [googleClassesForceDelete, setGoogleClassesForceDelete] = useState<boolean>(false);
  const [googleModalLoading, setGoogleModalLoading] = useState<boolean>(false);
  const [googleAddClassesModalShow, setGoogleAddClassesModalShow] = useState<boolean>(false);
  const [googleSyncClassesModalShow, setGoogleSyncClassesModalShow] = useState<boolean>(false);
  const [googleDeleteClassesModalShow, setGoogleDeleteClassesModalShow] = useState<boolean>(false);
  const [googleClassesAddResultModalShow, setGoogleClassesAddResultModalShow] =
    useState<boolean>(false);
  const [googleClassesSyncResultModalShow, setGoogleClassesSyncResultModalShow] =
    useState<boolean>(false);
  const [
    googleStudentExistsInAnotherGroupModalShow,
    setGoogleStudentExistsInAnotherGroupModalShow,
  ] = useState<boolean>(false);
  const [googleClassesDeleteWarningModalShow, setGoogleClassesDeleteWarningModalShow] =
    useState<boolean>(false);
  const [googleClassesDeleteResultModalShow, setGoogleClassesDeleteResultModalShow] =
    useState<boolean>(false);
  const [isStudentExistsInAnotherGroup, setIsStudentExistsInAnotherGroup] =
    useState<boolean>(false);
  const [emptyClassesModalShow, setEmptyClassesModalShow] = useState<EmptyModalState>({
    show: false,
    hasFilteredClasses: false,
  });
  const [googleErrorClassesModalShow, setGoogleErrorClassesModalShow] = useState<boolean>(false);
  const [googleErrorClasses, setGoogleErrorClasses] = useState<any>();
  const [studentsInOtherGroups, setStudentsInOtherGroups] = useState<
    { email: string; groupId: string | null }[]
  >([]);
  const [shouldShowClassCodeModal, setShouldShowClassCodeModal] = useState(false);
  const [dropdownClassCode, setDropdownClassCode] = useState('');
  const [showDropdownClassCodeModal, setShowDropdownClassCodeModal] = useState(false);
  const [showClassReport, setShowClassReport] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [selectedClassForReport, setSelectedClassForReport] = useState<{
    id: string;
    name: string;
    grade: string;
    totalStudents: number;
  } | null>(null);
  const [isNewClass, setIsNewClass] = useState(false);
  const isComponentActive = useRef<boolean>(false);

  const {
    allClassrooms,
    filterdClassrooms,
    classConfig,
    activeGroupId,
    onCreateClass,
    createOrSaveClass,
    onSelectClass,
    classFilters,
    setClassFilters,
    classFilterFields,
    classCascadingFilters,
    hasAnyClassFilter,
    showClassCreatedModal,
    classCreatedWithoutStudentsNotifyTick,
    classDeletedNotifyTick,
    onCloseClassCreatedModal,
    showCopyModal,
    setShowCopyModal,
    onCloseCopyModal,
    onShowCopyModal,
    onAbortAddClass,
    clearAddClassFlow,
    user,
    isSavingGroup,
    isCsvLoading,
    isCsvSending,
    setIsCsvLoading,
    csvFilesAdded,
    setCsvFilesAdded,
    showUploadCsvModal,
    showStudentsTableModal,
    addStudentsToNewGroup,
    onShowUploadCsvModal,
    onCloseUploadCsvModal,
    onShowStudentsTableModal,
    onBackStudentsTableModal,
    studentsData,
    setStudentsData,
    onCsvStudentsCreate,
    disableSubmitButton,
    setDisableSubmitButton,
    showNewStudentInput,
    setShowNewStudentInput,
    currentClassroom,
    isLoading,
    setIsLoading,
    fetchDataError,
    refreshStudentsData,
    studentsProgress,
    statistics,
    selectedStudent,
    isClassEdit,
    setIsClassEdit,
    onShowStudentModal,
    onHideStudentModal,
    isStudentDataLoading,
    showStudentDataModal,
    selectedStudentData,
    showStudentEditModal,
    onShowStudentEditModal,
    onCloseStudentEditModal,
    showUnitData,
    onShowUnitData,
    onHideUnitData,
    selectedUnit,
    editStudent,
    showCsvStudentsCreateError,
    setShowCsvStudentsCreateError,
    showExistingStudentsModal,
    onCloseExistingStudentsModal,
    onStudentDelete,
    isStudentDeleting,
    showStudentDeleteError,
    setShowStudentDeleteError,
    isClassDeleting,
    showClassDeleteError,
    setShowClassDeleteError,
    onClassDelete,
    classStudentsExtraDataResponse,
    groupDetailsFromApi,
    isClassesListLoading,
    classesListError,
    filteredClassesReportRows,
    refetchClassesList,
    desktopClassesReport,
  } = myClasses;

  const navigate = useNavigate();
  const organizationId = user?.metadata?.organizationDetails?.organizationId || '';
  const organizationName = user?.metadata?.organizationDetails?.organizationName || '';
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifType, setNotifType] = useState<'success' | 'error' | 'info'>('success');
  const [copiedClassId, setCopiedClassId] = useState<string | null>(null);

  const isGoogle = common.isGoogle(user);
  const isMGWebsite = common.isMGWebsite();
  const isClassCode = common.isClassCode(user);

  const handleClassCreated = () => {
    setShouldShowClassCodeModal(true);
  };

  useEffect(() => {
    if (classCreatedWithoutStudentsNotifyTick === 0) {
      return;
    }
    setNotifMessage(String(t('schoolsListPage.classCreated') ?? ''));
    setNotifType('success');
    setNotifOpen(true);
  }, [classCreatedWithoutStudentsNotifyTick, t]);

  useEffect(() => {
    if (classDeletedNotifyTick === 0) {
      return;
    }
    setNotifMessage(String(t('schoolDetailsPage.classes.classDeletedSuccessfully') ?? ''));
    setNotifType('success');
    setNotifOpen(true);
  }, [classDeletedNotifyTick, t]);

  const handleCloseClassCodeModal = () => {
    setShouldShowClassCodeModal(false);
    clearAddClassFlow();
    setIsClassEdit(false);
    setIsNewClass(true);
  };

  useEffect(() => {
    isComponentActive.current = true;

    return () => {
      isComponentActive.current = false;
    };
  }, []);

  useEffect(() => {
    if (studentsProgress) {
      setIsNewClass(false);
    }
  }, [studentsProgress]);

  const handleGoogleClassAction = async (action: GoogleClassAction) => {
    if (!user) return;
    setIsLoading(true);
    const classes = await dispatch(teacherDashboardThunks.fetchGoogleCourses(action)).unwrap();
    if (!classes) return;

    setGoogleClasses(classes);

    if (classes.length === 0) {
      setEmptyClassesModalShow({ show: true, hasFilteredClasses: filterdClassrooms.length > 0 });
      setIsLoading(false);
      return;
    }

    const modalMap = {
      create: setGoogleAddClassesModalShow,
      update: isStudentExistsInAnotherGroup
        ? setGoogleStudentExistsInAnotherGroupModalShow
        : setGoogleClassesSyncResultModalShow,
      delete: setGoogleDeleteClassesModalShow,
    };

    modalMap[action](true);
    setIsLoading(false);
  };

  const handleGoogleClassOperation = async (
    operation: GoogleClassOperation,
    selectedClasses: BaseGoogleCourses[]
  ) => {
    const operationConfig = {
      add: {
        action: (course: BaseGoogleCourses) => (course.deletionRequired ? 'delete' : 'create'),
        closeModal: setGoogleAddClassesModalShow,
        showResultModal: setGoogleClassesAddResultModalShow,
      },
      sync: {
        action: (course: BaseGoogleCourses) => (course.deletionRequired ? 'delete' : 'update'),
        closeModal: setGoogleSyncClassesModalShow,
        showResultModal: isStudentExistsInAnotherGroup
          ? setGoogleStudentExistsInAnotherGroupModalShow
          : setGoogleClassesSyncResultModalShow,
      },
      delete: {
        action: () => 'delete',
        closeModal: setGoogleDeleteClassesModalShow,
        showResultModal: setGoogleClassesDeleteResultModalShow,
      },
    } as const;

    setGoogleModalLoading(true);

    const syncClasses = selectedClasses.map(course => ({
      id: course.id,
      action: operationConfig[operation].action(course),
      name: course.name,
    }));

    const hasClassesToDelete = syncClasses.some(classItem => classItem.action === 'delete');

    if (hasClassesToDelete) {
      if (googleSyncClassesModalShow) {
        setGoogleClassesForceDelete(true);
      }
      operationConfig[operation].closeModal(false);
      setGoogleClassesDelete(syncClasses);
      setGoogleClassesDeleteWarningModalShow(true);
      setGoogleModalLoading(false);
    } else {
      try {
        const syncResponse = await dispatch(
          teacherDashboardThunks.setGoogleCourses(syncClasses)
        ).unwrap();

        if (!syncResponse?.resultByCourse) {
          setGoogleModalLoading(false);
          return;
        }

        const hasError = Object.values(syncResponse.resultByCourse).some(
          course => 'error' in course
        );

        const unregisteredStudentsInOtherGroups = Object.values(syncResponse.resultByCourse)
          .flatMap(course =>
            (course.unregisteredStudents ?? []).filter(student =>
              student.error?.includes('student exists in another group')
            )
          )
          .map(student => {
            const groupMatch = student.error?.match(/group:([0-9]+)/);
            const groupId = groupMatch ? groupMatch[1] : null;
            return {
              email: student.email,
              groupId,
            };
          });
        operationConfig[operation].closeModal(false);
        setGoogleModalLoading(false);
        setGoogleClasses([]);

        if (unregisteredStudentsInOtherGroups.length > 0) {
          setIsStudentExistsInAnotherGroup(true);
          setStudentsInOtherGroups(unregisteredStudentsInOtherGroups);
          setGoogleStudentExistsInAnotherGroupModalShow(true);
        } else if (hasError) {
          setGoogleErrorClasses(syncResponse);
          setGoogleErrorClassesModalShow(true);
        } else {
          setGoogleClassesSyncResultModalShow(true);
        }
      } catch (error) {
        commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
        setGoogleModalLoading(false);
      }
    }
  };

  const googleClassErrorSubmit = async () => {
    setGoogleErrorClassesModalShow(false);
    await dispatch(teacherDashboardThunks.fetchTeacherGroups());
  };

  const googleClassResultSubmit = async () => {
    setGoogleClassesAddResultModalShow(false);
    setGoogleClassesSyncResultModalShow(false);
    setGoogleStudentExistsInAnotherGroupModalShow(false);
    setGoogleClassesDeleteResultModalShow(false);
    await dispatch(teacherDashboardThunks.fetchTeacherGroups());
    void refetchClassesList();
  };

  const onCloseEmptyClassesModal = useCallback(() => {
    setEmptyClassesModalShow(prev => ({
      ...prev,
      show: false,
    }));

    setTimeout(() => {
      setEmptyClassesModalShow(prev => ({
        ...prev,
        hasFilteredClasses: false,
      }));
    }, 400);
  }, []);

  const onSubmitDeleteClassesModal = useCallback(async () => {
    setGoogleModalLoading(true);
    await dispatch(teacherDashboardThunks.setGoogleCourses(googleClassesDelete)).unwrap();
    setGoogleClasses([]);
    setGoogleClassesDelete([]);
    setGoogleClassesDeleteWarningModalShow(false);
    setGoogleClassesDeleteResultModalShow(true);
    setGoogleStudentExistsInAnotherGroupModalShow(false);
    setGoogleModalLoading(false);
    setGoogleClassesForceDelete(false);
  }, [googleClassesDelete, dispatch]);

  const onCancelDeleteClassesModal = useCallback(async () => {
    setGoogleClasses([]);
    setGoogleClassesDelete([]);
    setGoogleClassesDeleteWarningModalShow(false);
    setGoogleClassesForceDelete(false);
  }, [googleClassesDelete, dispatch]);

  const emptyClassesModal = (
    <GoogleEmptyClassesModal
      show={emptyClassesModalShow.show}
      hasFilteredClasses={emptyClassesModalShow.hasFilteredClasses}
      onSubmit={onCloseEmptyClassesModal}
    />
  );

  const googleDeleteClassConfirmationModal = (
    <GoogleDeleteClassConfirmationModal
      show={googleClassesDeleteWarningModalShow}
      hasFilteredClasses={emptyClassesModalShow.hasFilteredClasses}
      googleClassesDelete={googleClassesDelete}
      isForced={googleClassesForceDelete}
      isLoading={googleModalLoading}
      onSubmit={onSubmitDeleteClassesModal}
      onCancel={onCancelDeleteClassesModal}
    />
  );

  const errorClassesModal = (
    <GoogleErrorClassesModal
      show={googleErrorClassesModalShow}
      classes={googleErrorClasses}
      onClose={googleClassErrorSubmit}
    />
  );

  const classExistingStudentsModal = (
    <Transition
      in={!!showExistingStudentsModal}
      timeout={400}
      mountOnEnter
      unmountOnExit
      children={state => {
        return (
          <>
            <AppBackdrop className={classes(classNames.backdrop, `backdrop-${state}`)} />
            <AppModal>
              <div
                className={classes(
                  classNames.modal,
                  classNames.existingStudentsModal,
                  classNames.fileUploadModal,
                  `fade-${state}`
                )}
              >
                <div className={classNames.existingStudentsModalHeader}>
                  <h2>
                    {showExistingStudentsModal?.addedStudents
                      ? t('settings.add_students.partial_success')
                      : t('settings.add_students.enrollment_unsuccessful')}
                  </h2>
                  {!!showExistingStudentsModal?.addedStudents && (
                    <div
                      className={classNames.description}
                      dangerouslySetInnerHTML={{
                        __html: t(
                          'settings.add_students.partial_success_description',
                          showExistingStudentsModal
                        ).toString(),
                      }}
                    />
                  )}
                  <div className={classNames.description}>
                    {t('settings.add_students.enrollment_unsuccessful_description')}
                  </div>
                </div>

                <div className={classNames.existingStudentsList}>
                  <div className={classNames.existingStudentsHead}>
                    <div>{t('firstname')}</div>
                    <div>{t('lastname')}</div>
                    <div>{t('email')}</div>
                  </div>
                  <div className={classNames.existingStudentsBody}>
                    {showExistingStudentsModal?.existingStudentsList?.map((student, index) => (
                      <div
                        key={`existingStudentsListItem--${index}`}
                        className={classNames.existingStudentsListItem}
                      >
                        <div>{student.firstName}</div>
                        <div>{student.lastName}</div>
                        <div>{student.email}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={classNames.buttons}>
                  <TheButton
                    className={classNames.btn}
                    text={t('ok')}
                    shouldRecolor={false}
                    emitOnClick={onCloseExistingStudentsModal}
                  />
                </div>
              </div>
            </AppModal>
          </>
        );
      }}
    />
  );

  const classCreatedModal = (
    <Transition
      in={showClassCreatedModal}
      timeout={400}
      mountOnEnter
      unmountOnExit
      children={state => {
        return (
          <>
            <AppBackdrop className={classes(classNames.backdrop, `backdrop-${state}`)} />
            <AppModal>
              <div className={classes(classNames.modal, classNames.copyModal, `fade-${state}`)}>
                <h2>
                  {addStudentsToNewGroup
                    ? t('schoolsListPage.classCreated')
                    : t('settings.add_students.studentsSuccessfullyEnrolled')}
                </h2>
                <span
                  className={classNames.description}
                  dangerouslySetInnerHTML={{
                    __html: t(
                      classConfig?.placementTestRequired ??
                        LEVEL_CONTROL_DEFAULTS.placementTestRequired
                        ? 'settings.add_students.classCreatedDescription'
                        : 'settings.add_students.classCreatedDescriptionNoLevelTest'
                    ).toString(),
                  }}
                />
                <TheButton
                  className={classNames.btn}
                  text={t('done')}
                  shouldRecolor={false}
                  emitOnClick={onCloseClassCreatedModal}
                />
              </div>
            </AppModal>
          </>
        );
      }}
    />
  );

  const linkCopiedModal = (
    <Transition
      in={showCopyModal}
      timeout={400}
      mountOnEnter
      unmountOnExit
      children={state => {
        return (
          <>
            <AppBackdrop className={classes(classNames.backdrop, `backdrop-${state}`)} />
            <AppModal>
              <div className={classes(classNames.modal, classNames.copyModal, `fade-${state}`)}>
                <h2>{t('linkCopied')}</h2>
                <span className={classNames.description}>
                  {t('goAheadAndShareThisLinkWithYourStudentsNow')}
                </span>
                <TheButton
                  className={classNames.btn}
                  text={t('done')}
                  shouldRecolor={false}
                  emitOnClick={onCloseCopyModal}
                />
              </div>
            </AppModal>
          </>
        );
      }}
    />
  );

  const fetchDataErrorModal = (
    <CommunicationProblemModal
      show={!!fetchDataError}
      onRefresh={refreshStudentsData}
      error={fetchDataError}
    />
  );

  const googleModalsConfig = {
    add: {
      show: googleAddClassesModalShow,
      setShow: setGoogleAddClassesModalShow,
      props: {},
    },
    sync: {
      show: googleSyncClassesModalShow,
      setShow: setGoogleSyncClassesModalShow,
      props: { sync: true },
    },
    delete: {
      show: googleDeleteClassesModalShow,
      setShow: setGoogleDeleteClassesModalShow,
      props: { deleteClasses: true },
    },
  } as const;

  const googleClassesModals = Object.entries(googleModalsConfig).map(([key, config]) => (
    <GoogleClassesModal
      key={key}
      show={config.show}
      classes={googleClasses}
      isLoading={googleModalLoading}
      onSubmit={filteredClasses =>
        handleGoogleClassOperation(key as GoogleClassOperation, filteredClasses)
      }
      onClose={() => config.setShow(false)}
      {...config.props}
    />
  ));

  const googleResultModals = {
    add: {
      show: googleClassesAddResultModalShow,
      props: {},
    },
    sync: {
      show: googleClassesSyncResultModalShow,
      props: { sync: true },
    },
    delete: {
      show: googleClassesDeleteResultModalShow,
      props: { deleteClasses: true },
    },
    studentsInAnotherGroup: {
      show: googleStudentExistsInAnotherGroupModalShow,
      props: { studentsInAnotherGroup: true, students: studentsInOtherGroups },
    },
  } as const;

  const googleClassesResultModals = Object.entries(googleResultModals).map(([key, config]) => (
    <GoogleClassesResultModal
      key={key}
      show={config.show}
      onSubmit={googleClassResultSubmit}
      {...config.props}
    />
  ));

  const createClassHandler = async () => {
    if (isOrganizationDetailsLoading) {
      return;
    }

    setIsOrganizationDetailsLoading(true);

    let isAllowedToRegister: boolean = false;

    try {
      isAllowedToRegister = !(await registerService.isRegistrationLimitReached(user))?.data.data;
    } catch (error: any) {
      const response = error?.response;
      const status = response?.status;
      const message = response?.data?.message;

      if (
        status === 403 &&
        message === 'User registration limit reached. No new users can be registered at this time.'
      ) {
        isAllowedToRegister = false;
      } else {
        commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
        setIsOrganizationDetailsLoading(false);
        return;
      }
      setIsOrganizationDetailsLoading(false);
    }

    if (!isComponentActive.current) {
      return;
    }

    setIsOrganizationDetailsLoading(false);
    setAllowAddingStudents(isAllowedToRegister);

    onCreateClass();

    if (setIsClassEdit) {
      setIsClassEdit(true);
    }
  };

  const onCloseDealLimitReachedModal = useCallback(() => {
    setShowDealLimitReachedModal(false);
  }, []);

  const handleViewClass = useCallback(
    (classId: string) => {
      navigate(`/dashboard/my-classes/${classId}`);
    },
    [navigate]
  );

  const handleEditClass = useCallback(
    (classId: string) => {
      navigate(`/dashboard/my-classes/edit-class/${classId}`);
    },
    [navigate]
  );

  const handleAddStudent = useCallback(
    async (classId: string) => {
      setIsOrganizationDetailsLoading(true);

      let isAllowedToRegister: boolean = false;

      try {
        isAllowedToRegister = !(await registerService.isRegistrationLimitReached(user))?.data.data;
      } catch (error: any) {
        const response = error?.response;
        const status = response?.status;
        const message = response?.data?.message;

        if (
          status === 403 &&
          message ===
            'User registration limit reached. No new users can be registered at this time.'
        ) {
          setShowDealLimitReachedModal(true);
        } else {
          commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
        }
        setIsOrganizationDetailsLoading(false);
        return;
      }

      if (!isComponentActive.current) {
        return;
      }

      setIsOrganizationDetailsLoading(false);

      if (!isAllowedToRegister) {
        setShowDealLimitReachedModal(true);
        return;
      }

      const classRow = desktopClassesReport?.tableData?.find(classroom => classroom.id === classId);
      if (!classRow) {
        setNotifMessage(t('error.somethingWentWrong') || '');
        setNotifType('error');
        setNotifOpen(true);
        setTimeout(() => setNotifOpen(false), 2000);
        return;
      }

      if (classRow.classCode) {
        setDropdownClassCode(classRow.classCode);
        setShowDropdownClassCodeModal(true);
        return;
      }

      const selectedClass =
        filterdClassrooms.find(classroom => classroom.id === classId) ||
        allClassrooms.find(classroom => classroom.id === classId) ||
        classesReportTableRowToTeacherGroup(classRow);

      onSelectClass(selectedClass);
      onShowUploadCsvModal(false);
    },
    [
      desktopClassesReport?.tableData,
      t,
      filterdClassrooms,
      allClassrooms,
      onSelectClass,
      onShowUploadCsvModal,
    ]
  );

  const handleDownloadReport = useCallback(
    (classId: string) => {
      const classRow = desktopClassesReport?.tableData?.find(classroom => classroom.id === classId);
      if (!classRow) {
        setNotifMessage(t('error.somethingWentWrong') || '');
        setNotifType('error');
        setNotifOpen(true);
        setTimeout(() => setNotifOpen(false), 2000);
        return;
      }

      setSelectedClassForReport({
        id: classId,
        name: classRow.name,
        grade: String(classRow.gradeId || ''),
        totalStudents: classRow.students,
      });
      setShowClassReport(true);
      setLoadingReport(true);
    },
    [desktopClassesReport?.tableData, t]
  );

  const [showDeleteClassModal, setShowDeleteClassModal] = useState(false);
  const [deleteClassTarget, setDeleteClassTarget] = useState<{
    id: string;
    activeStudentsCount: number;
  } | null>(null);

  const handleDeleteClass = useCallback(
    (classId: string) => {
      if (isClassDeleting) {
        return;
      }
      const classRow = desktopClassesReport?.tableData?.find(classroom => classroom.id === classId);
      setDeleteClassTarget({
        id: classId,
        activeStudentsCount: classRow?.activeStudents ?? 0,
      });
      setShowDeleteClassModal(true);
    },
    [isClassDeleting, desktopClassesReport?.tableData]
  );

  const closeDeleteClassModal = useCallback(() => {
    if (isClassDeleting) {
      return;
    }
    setShowDeleteClassModal(false);
    setDeleteClassTarget(null);
  }, [isClassDeleting]);

  const confirmDeleteClass = useCallback(() => {
    if (!deleteClassTarget?.id) {
      return;
    }
    const classId = deleteClassTarget.id;
    setShowDeleteClassModal(false);
    setDeleteClassTarget(null);
    void onClassDelete(undefined, classId);
  }, [deleteClassTarget, onClassDelete]);

  const dropdownItems = useMemo(
    () => [
      {
        label: t('schoolDetailsPage.classes.actions.viewClass'),
        action: 'view',
        onClick: handleViewClass,
      },
      {
        label: t('schoolDetailsPage.classes.actions.addStudent'),
        action: 'add',
        onClick: handleAddStudent,
      },
      {
        label: t('schoolDetailsPage.classes.actions.downloadReport'),
        action: 'download',
        onClick: handleDownloadReport,
      },
      {
        label: t('schoolDetailsPage.classes.actions.edit'),
        action: 'edit',
        onClick: handleEditClass,
      },
      {
        label: t('schoolDetailsPage.classes.actions.delete'),
        action: 'delete',
        danger: true,
        onClick: handleDeleteClass,
      },
    ],
    [t, handleViewClass, handleAddStudent, handleDownloadReport, handleEditClass, handleDeleteClass]
  );

  const columns = useMemo(
    () =>
      createAdminClassesReportColumns({
        t,
        copiedClassId,
        setCopiedClassId,
        onNameClick: handleViewClass,
        onCopyClassCode: message => {
          setNotifMessage(message);
          setNotifType('success');
          setNotifOpen(true);
          setTimeout(() => setNotifOpen(false), 2000);
        },
        dropdownItems,
        alwaysShowPrimaryRowAction: true,
        currentTeacherUserId: user?.id,
      }),
    [t, copiedClassId, dropdownItems, user?.id]
  );

  const tableName = useMemo(() => t('schoolDetailsPage.classes.title') || '', [t]);

  const onLoadReportError = useCallback(() => {
    setLoadingReport(false);
    setShowClassReport(false);
    commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
  }, []);

  const handleReportGenerated = useCallback(() => {
    setLoadingReport(false);
    setShowClassReport(false);
  }, []);

  const onDownload = useCallback(async () => {
    const dr = desktopClassesReport;
    if (!organizationId || !dr) return;

    let classesData: ReturnType<typeof normalizeGroupReportItemsToTableRows> = [];
    try {
      const pageQueryParam = dr.buildClassQueryParams(1);
      pageQueryParam.page = 0;
      let hasNextPage = true;

      while (hasNextPage) {
        pageQueryParam.page++;
        const response = await classService.getAllClasses(organizationId, pageQueryParam);
        if (response?.data?.items?.length) {
          classesData = [
            ...classesData,
            ...normalizeGroupReportItemsToTableRows(response.data.items, t),
          ];
        }
        hasNextPage = !!response?.data?.hasNextPage;
      }
    } catch {
      /* ignore */
    }

    const xlsxColumns = columns.filter(c => !!c.xlsxRender);
    const headerRow = xlsxColumns.map(c => c.xlsxLabel || c.label);
    const xlsxData: XlsxData = {
      fileName: tableName,
      tables: [],
    };
    const skillTable: XlsxTable = {
      name: tableName,
      headerRow: [...headerRow],
      data: [],
    };
    classesData.forEach(l => {
      const row: Record<string, unknown> = {};
      xlsxColumns.forEach(col => {
        const formattedValue = col.xlsxRender?.(
          (l as Record<string, unknown>)[col.key],
          l as never
        );
        row[col.xlsxLabel || col.label] = formattedValue;
      });
      skillTable.data.push(row);
    });
    xlsxData.tables.push(skillTable);
    processing.createXlsx(xlsxData);
  }, [organizationId, desktopClassesReport, columns, tableName, t]);

  const dr = desktopClassesReport;
  const desktopTotal = dr?.paginationInfo.totalCount ?? 0;
  const hasActiveSearch = (dr?.searchQuery || '').trim().length > 0;

  const classesListSkeleton =
    !!dr &&
    (dr.isFirstLoading ||
      dr.isLoadingClasses ||
      (isLoading && !showStudentDataModal && !isClassEdit));

  const viewMode = isClassEdit
    ? 'edit'
    : dr
      ? desktopTotal === 0 && !classesListSkeleton && !hasAnyClassFilter && !hasActiveSearch
        ? 'noClasses'
        : 'listAdmin'
      : 'listAdmin';
  const isAddClassView = viewMode === 'edit' && !classConfig?.id;

  const content =
    viewMode === 'noClasses' ? (
      <NoClasses
        className={classNames.noClasses}
        emitCreateClass={onCreateClass}
        setIsClassEdit={setIsClassEdit}
        googleCreateClass={() => handleGoogleClassAction('create')}
        isGoogle={isGoogle}
        isMGWebsite={isMGWebsite}
        isClassCode={isClassCode}
      />
    ) : viewMode === 'edit' ? (
      classConfig &&
      (!classConfig.id ? (
        <div className={classNames.createClassInset}>
          <button
            type='button'
            className={classNames.createClassBack}
            onClick={onAbortAddClass}
            disabled={isSavingGroup || (!!isLoading && !showStudentDataModal)}
          >
            <SVG src={leftArrow} className={classNames.createClassBackIcon} />
            {t('translation:back')}
          </button>
          <div className={classNames.createClassPageHeader}>
            <h1 className={classNames.createClassPageTitle}>{t('translation:addNewClass')}</h1>
          </div>
          <ClassEditor
            key={classConfig?.id}
            classConfig={classConfig}
            currentClassroom={currentClassroom}
            createOrSaveClass={createOrSaveClass}
            loading={isSavingGroup || (!!isLoading && !showStudentDataModal)}
            showCopyModal={showCopyModal}
            onShowCopyModal={onShowCopyModal}
            setShowCopyModal={setShowCopyModal}
            onAbortAddClass={onAbortAddClass}
            onShowUploadCsvModal={onShowUploadCsvModal}
            setIsClassEdit={setIsClassEdit}
            isClassDeleting={isClassDeleting}
            showClassDeleteError={showClassDeleteError}
            setShowClassDeleteError={setShowClassDeleteError}
            onClassDelete={onClassDelete}
            onClassCreated={handleClassCreated}
            allowAddingStudents={allowAddingStudents}
          />
        </div>
      ) : (
        <ClassEditor
          key={classConfig?.id}
          classConfig={classConfig}
          currentClassroom={currentClassroom}
          createOrSaveClass={createOrSaveClass}
          loading={isSavingGroup || (!!isLoading && !showStudentDataModal)}
          showCopyModal={showCopyModal}
          onShowCopyModal={onShowCopyModal}
          setShowCopyModal={setShowCopyModal}
          onAbortAddClass={onAbortAddClass}
          onShowUploadCsvModal={onShowUploadCsvModal}
          setIsClassEdit={setIsClassEdit}
          isClassDeleting={isClassDeleting}
          showClassDeleteError={showClassDeleteError}
          setShowClassDeleteError={setShowClassDeleteError}
          onClassDelete={onClassDelete}
          onClassCreated={handleClassCreated}
        />
      ))
    ) : viewMode === 'listAdmin' && dr ? (
      <>
        <div
          className={classes(
            adminClassStyles.teachersMode,
            !dr.tableData.length && !hasAnyClassFilter ? adminClassStyles.emptyStateMode : ''
          )}
        >
          {dr.classesError ? (
            <div>{t('schoolDetailsPage.classes.errorLoading', { error: dr.classesError })}</div>
          ) : null}
          <div
            className={classes(
              adminClassStyles.classesDetailsCard,
              dr.tableData.length === 0 ? adminClassStyles.emptyStateCard : ''
            )}
          >
            <GenericTable
              title={String(tableName)}
              onDownload={onDownload}
              downloadFileName={String(tableName)}
              serverSideDownload={true}
              hasData={hasAnyClassFilter || dr.tableData.length > 0}
              emptyStateTitle={
                classesListSkeleton || hasAnyClassFilter || hasActiveSearch
                  ? ' '
                  : t('schoolDetailsPage.classes.zeroState.title') || ''
              }
              emptyStateDescription={
                classesListSkeleton
                  ? ' '
                  : hasActiveSearch
                    ? t('genericTable.noResultsForSearchQuery', {
                        searchQuery: dr?.searchQuery || '',
                      }) || ''
                    : hasAnyClassFilter
                      ? t('schoolDetailsPage.classes.zeroState.filterTitle') || ''
                      : t('schoolDetailsPage.classes.zeroState.description') || ''
              }
              emptyStateSubDescription={
                hasAnyClassFilter && !classesListSkeleton
                  ? t('schoolDetailsPage.classes.zeroState.filterDescription') || ''
                  : undefined
              }
              emptyStateAction={
                !isMGWebsite && !hasAnyClassFilter && !classesListSkeleton && !hasActiveSearch ? (
                  <Button
                    onClick={createClassHandler}
                    className={adminClassStyles.zeroStateActionButton}
                  >
                    {t('schoolDetailsPage.classes.zeroState.buttonText')}
                  </Button>
                ) : undefined
              }
              columns={columns}
              data={dr.tableData}
              rowKey={(cls: { id: string }) => cls.id}
              variant='compact'
              className={adminClassStyles.classesTable}
              pageSize={CLASSES_REPORT_PAGE_SIZE}
              showSortArrows={true}
              showLoader={classesListSkeleton}
              serverSidePagination={true}
              totalPages={dr.paginationInfo.totalPages}
              totalCount={dr.paginationInfo.totalCount}
              currentPage={dr.currentPage}
              onPageChange={dr.setCurrentPage}
              serverSideSorting={true}
              sortBy={dr.sortBy}
              sortDirection={dr.sortDirection}
              onSortChange={dr.onSortChange}
              showSearch={true}
              searchPlaceholder={
                t('schoolDetailsPage.classes.searchPlaceholder') || 'Search classes'
              }
              showHeaderInEmptyState={classesListSkeleton || hasAnyClassFilter || hasActiveSearch}
              onSearchQueryChange={query => {
                dr?.setSearchQuery(query);

                if ((query || '').trim()) {
                  dr?.onSortChange?.(' ', 'asc');
                } else if (!(dr?.sortBy || '').trim()) {
                  dr?.onSortChange?.('name', 'asc');
                }

                dr?.setCurrentPage?.(1);
              }}
            />
          </div>
        </div>
      </>
    ) : null;

  return (
    <div className={isAddClassView ? undefined : classNames.myClassesView}>
      {classExistingStudentsModal}
      {classCreatedModal}
      {linkCopiedModal}
      {fetchDataErrorModal}

      <ClassModalsManager
        showUploadModal={showUploadCsvModal}
        setShowUploadModal={show => {
          if (show) {
            onShowUploadCsvModal(false);
          } else {
            onCloseUploadCsvModal();
          }
        }}
        addUsersToNewEntity={addStudentsToNewGroup}
        setAddUsersToNewEntity={() => {}}
        onUsersCreate={() => {}}
        setShowUsersCreateError={() => {}}
        groupId={currentClassroom?.id || ''}
        groupName={currentClassroom?.name || ''}
        dealId={user?.metadata?.organizationDetails?.dealId}
        isTeacher={false}
        usersInClass={currentClassroom?.totalStudents || 0}
        placementTestRequired={
          classConfig?.placementTestRequired ?? LEVEL_CONTROL_DEFAULTS.placementTestRequired
        }
        onStayPageStudentAddComplete={() => {
          eventDispatcherContext.dispatch(DispatcherEvents.SCHOOL_DETAILS_CHANGE, null);

          if (addStudentsToNewGroup && activeGroupId) {
            onCloseClassCreatedModal();
            navigate(`/dashboard/my-classes/${activeGroupId}`);
            return;
          }

          navigate(`/dashboard/my-classes/${currentClassroom?.id}`);
        }}
      />

      <ActionNotification
        open={notifOpen}
        message={notifMessage}
        type={notifType}
        position='top-center'
        onClose={() => setNotifOpen(false)}
      />
      <ClassCodeModal
        open={shouldShowClassCodeModal}
        classCode={currentClassroom?.classCode ?? ''}
        invitationUrl={currentClassroom?.invitationLinkForStudents}
        onClose={handleCloseClassCodeModal}
      />
      <ClassCodeModal
        open={showDropdownClassCodeModal}
        classCode={dropdownClassCode}
        onClose={() => {
          setShowDropdownClassCodeModal(false);
          setDropdownClassCode('');
        }}
      />
      {isGoogle && (
        <>
          {googleClassesModals}
          {googleClassesResultModals}
          {emptyClassesModal}
          {googleDeleteClassConfirmationModal}
          {errorClassesModal}
        </>
      )}
      <DealLimitReachedMessage
        show={showDealLimitReachedModal}
        onClose={onCloseDealLimitReachedModal}
      />
      {isOrganizationDetailsLoading && <LoaderModal show={true} />}
      {loadingReport && <LoaderModal show={true} />}
      {showClassReport && selectedClassForReport && (
        <ClassReport
          schoolId={organizationId}
          classId={selectedClassForReport.id}
          className={selectedClassForReport.name}
          classGrade={selectedClassForReport.grade}
          totalStudents={selectedClassForReport.totalStudents}
          onLoadReportError={onLoadReportError}
          onReportGenerated={handleReportGenerated}
        />
      )}
      <DeleteClassModal
        show={showDeleteClassModal}
        onClose={closeDeleteClassModal}
        onConfirm={confirmDeleteClass}
        isLoading={isClassDeleting}
        activeStudentsCount={deleteClassTarget?.activeStudentsCount ?? 0}
      />
      {viewMode !== 'edit' && (
        <div id='my_classes_header' className={classNames.header}>
          <div className={classNames.headerTitle}>
            <h2>{t('settings.my_classes')}</h2>
            {organizationName && <p className={classNames.headerSubTitle}>{organizationName}</p>}
          </div>
          <div className={classNames.headerToolbar}>
            {!isGoogle && organizationId ? (
              <div className={classNames.filtersBarSlot}>
                <PageHeaderFiltersBar
                  filterFields={classFilterFields}
                  filters={classFilters}
                  setFilters={setClassFilters}
                  organizationId={organizationId}
                  cascadingFilters={classCascadingFilters}
                  className={classNames.myClassesFiltersBar}
                  filterAnchorStayInView={false}
                  disabled={viewMode === 'noClasses'}
                />
              </div>
            ) : null}
            <div className={classNames.headerToolbarActions}>
              {isGoogle && (
                <TheButton
                  className={classNames.syncNewClassBtn}
                  text={t('Sync')}
                  icon={syncIcon}
                  shouldRecolor={false}
                  plain={true}
                  emitOnClick={() => handleGoogleClassAction('update')}
                />
              )}
              {!isMGWebsite && (
                <TheButton
                  className={classNames.createNewClassBtn}
                  text={isGoogle ? t('translation:importClasses') : t('translation:createNewClass')}
                  shouldRecolor={false}
                  emitOnClick={
                    isGoogle ? () => handleGoogleClassAction('create') : createClassHandler
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}
      {isGoogle && filterdClassrooms.length > 0 && viewMode === 'listAdmin' ? (
        <div className={classNames.googleDeleteRow}>
          <TheButton
            text={t('deleteClasses')}
            icon={deleteIcon}
            shouldRecolor={false}
            plain={true}
            emitOnClick={() => handleGoogleClassAction('delete')}
          />
        </div>
      ) : null}
      <div className={classes(classNames.body, !isAddClassView && classNames.bodyFullWidth)}>
        {(viewMode === 'listAdmin' || viewMode === 'noClasses') && user ? (
          <TeacherMyClassesStatistics
            user={user}
            classFilters={classFilters}
            hasAnyClassFilter={hasAnyClassFilter}
          />
        ) : null}
        <div className={classes(classNames.viewBlock, classNames[`viewBlock_${viewMode}`])}>
          {content}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MyClassesViewDesktop);
